import { NgxSpinnerService } from 'ngx-spinner';
import { injectUrlViewState } from '../../../utils/url-view-state.util';
import { FNFResponse } from '../../../models/response';
import { DeviceService } from './../../../services/device.service';
import { Component, inject } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { CommonModule } from '@angular/common';

@Component({
  selector: 'app-device',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './device.component.html',
  styleUrl: './device.component.css',
})
export class DeviceComponent {
  deviceService = inject(DeviceService);
  ngxSpinnerService = inject(NgxSpinnerService);
  toastr = inject(ToastrService);
  devices: any[] = [];
  displayLoading: boolean = false;
  viewMode: 'grid' | 'list' = 'list';
  /** List / Grid and Group by live in the URL (?view, ?group). */
  private urlState = injectUrlViewState();
  searchTerm: string = '';
  groupBy: 'none' | 'device_type' | 'station' = 'none';

  readonly groupOptions = [
    { value: 'none', label: 'None' },
    { value: 'device_type', label: 'Device Type' },
    { value: 'station', label: 'Station' },
  ];

  get groupByLabel(): string {
    return this.groupOptions.find((option) => option.value === this.groupBy)?.label || 'None';
  }

  onGroupByChange(event: any) {
    this.groupBy = event.target.value;
    this.urlState.set('group', this.groupBy, 'none');
  }

  /** Filtered devices bucketed by the active grouping, sorted by group name. */
  get deviceGroups(): { label: string; devices: any[] }[] {
    if (this.groupBy === 'none') {
      return [{ label: '', devices: this.filteredDevices }];
    }

    const buckets = new Map<string, any[]>();
    this.filteredDevices.forEach((device: any) => {
      const label = this.groupLabel(device);
      if (!buckets.has(label)) {
        buckets.set(label, []);
      }
      buckets.get(label)!.push(device);
    });

    return Array.from(buckets.entries())
      .map(([label, devices]) => ({ label, devices }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }

  private groupLabel(device: any): string {
    if (this.groupBy === 'station') {
      return device.station?.station_name || 'Unassigned';
    }
    return device.device_type || 'Other';
  }

  get groupCount(): number {
    return this.groupBy === 'none' ? 0 : this.deviceGroups.length;
  }

  get filteredDevices(): any[] {
    const term = this.searchTerm.trim().toLowerCase();
    if (!term) {
      return this.devices;
    }
    return this.devices.filter(
      (device: any) =>
        device.device_name?.toLowerCase().includes(term) ||
        device.device_type?.toLowerCase().includes(term) ||
        device.station?.station_name?.toLowerCase().includes(term),
    );
  }

  /** Distinct stations represented in the filtered list. */
  get stationCount(): number {
    return new Set(
      this.filteredDevices.map((device: any) => device.station?.station_name).filter(Boolean),
    ).size;
  }

  onSearch(event: any) {
    this.searchTerm = event.target.value;
  }

  deviceIcon(type: string): string {
    switch ((type || '').toLowerCase()) {
      case 'printer':
        return 'bx bx-printer';
      case 'kds':
        return 'bx bx-desktop';
      case 'kiosk':
        return 'bx bx-tab';
      case 'payment terminal':
        return 'bx bx-credit-card';
      default:
        return 'bx bx-mobile-alt';
    }
  }

  statusTone(status: string): string {
    const value = (status || '').toLowerCase();
    if (value === 'active' || value === 'online') {
      return 'online';
    }
    if (value === 'maintenance') {
      return 'maintenance';
    }
    return 'offline';
  }

  ngOnInit() {
    this.viewMode = this.urlState.pick<'grid' | 'list'>('view', ['grid', 'list'], 'list');
    this.groupBy = this.urlState.pick<'none' | 'device_type' | 'station'>(
      'group',
      ['none', 'device_type', 'station'],
      'none',
    );
    this.getDeviceList();
  }

  /** List / Grid, kept in the URL (?view) so a browser reload keeps it. */
  setViewMode(mode: 'grid' | 'list') {
    this.viewMode = mode;
    this.urlState.set('view', mode, 'list');
  }

  getDeviceList() {
    this.displayLoading = true;
    this.deviceService.getDevices().subscribe({
      next: (response: FNFResponse<any>) => {
        if (!response.data && response.error) {
          this.toastr.error(response.error.error?.message || response.error.message, 'Error');
          this.displayLoading = false;
          return;
        } else if (!response.data) {
          this.toastr.error('No data found!', 'Error');
          this.displayLoading = false;
          return;
        }
        this.devices = response.data;
        this.devices.sort((a: any, b: any) => a.device_name.localeCompare(b.device_name));
        this.displayLoading = false;
      },
      error: () => {
        this.toastr.error('Unable to complete your action. Please try again later.', 'Error');
        this.displayLoading = false;
      },
    });
  }
}
